import { useCallback, useState } from 'react'
import { showToast } from '@quickmd-llc/design-system'
import { client } from 'api/components/queryClient'
import {
  getGetPatientsPatientIdIdentityverificationQueryKey,
  getPatientsPatientIdIdentityverification,
  useGetPatientsPatientId,
} from 'api/generated/patient/patient'
import { fetchPatientMediaFile } from '../../utils'
import { windowOpenArgs } from 'utils/windowOpenArgs'
import { EventLocations } from 'lib/segment/constants'
import { useSelector } from 'store/hooks/useSelector'
import { useSingleFlightCallback } from 'hooks/useSingleFlightCallback'
import { addSentryBreadcrumb } from 'utils/sentryUtility'

// After opening a resource, briefly ignore repeat clicks. A sticky/bouncing mouse
// button (or a click-and-hold the OS registers as several clicks) would otherwise
// fire these async openers multiple times (repeat fetches / duplicate tabs).
const OPEN_COOLDOWN_MS = 500

interface UsePatientCardActionsInput {
  patientId: string | undefined
  // Callers pass heterogeneous patient shapes (PatientWithVisitInfo and
  // Partial<PatientStaffApiOutContract>), so this stays loosely typed. Pre-existing;
  // properly typing it is a cross-call-site refactor out of scope for this change.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patient?: Record<string, any>
}

export function usePatientCardActions({ patientId, patient }: UsePatientCardActionsInput) {
  const showVisitWave = useSelector((state) => state.waitingRoomReducer.showVisitWave)

  const cachedPatient =
    useGetPatientsPatientId(patientId || '', {
      query: { enabled: false },
    }).data || patient

  const [isDrugScreenOpen, setIsDrugScreenOpen] = useState(false)
  const [drugScreenView, setDrugScreenView] = useState<'empty' | 'upload' | 'onFile'>('onFile')
  const [isIdVerificationOpen, setIsIdVerificationOpen] = useState(false)

  // Guard the async openers with a single-flight + cooldown so a sticky/bouncing
  // click can't fire several fetches / open several tabs. Guarding here covers
  // every consumer of this hook (e.g. the drug-screen badge in VisitPatientHeader).
  const { run: openDrugScreen, isRunning: isDrugScreenOpening } = useSingleFlightCallback(
    useCallback(async () => {
      const fileId = cachedPatient?.DrugScreen?.FileId
      if (!fileId) {
        // No file on record — open straight to the empty/upload view, no fetch needed.
        setDrugScreenView('empty')
        setIsDrugScreenOpen(true)
        return
      }
      const response = await fetchPatientMediaFile(cachedPatient?.PatientId, fileId)
      if (response) {
        addSentryBreadcrumb({
          category: 'patient-resource-open',
          message: 'Opened drug screen file',
          level: 'info',
          data: { feature: 'drug-screen', patientId: cachedPatient?.PatientId },
        })
        setDrugScreenView('onFile')
        setIsDrugScreenOpen(true)
      }
    }, [cachedPatient?.PatientId, cachedPatient?.DrugScreen?.FileId]),
    { cooldownMs: OPEN_COOLDOWN_MS },
  )

  const closeDrugScreen = useCallback(() => setIsDrugScreenOpen(false), [])

  const { run: openIdVerification, isRunning: isIdVerificationOpening } = useSingleFlightCallback(
    useCallback(
      () =>
        getPatientsPatientIdIdentityverification(cachedPatient?.PatientId)
          .then((response) => {
            const queryKey = getGetPatientsPatientIdIdentityverificationQueryKey(cachedPatient?.PatientId)
            client.setQueryData(queryKey, response)
            addSentryBreadcrumb({
              category: 'patient-resource-open',
              message: 'Opened ID verification',
              level: 'info',
              data: { feature: 'id-verification', patientId: cachedPatient?.PatientId },
            })
            setIsIdVerificationOpen(true)
          })
          .catch(() => {
            showToast({ type: 'error', message: 'Failed to load ID verification.' })
          }),
      [cachedPatient?.PatientId],
    ),
    { cooldownMs: OPEN_COOLDOWN_MS },
  )

  const closeIdVerification = useCallback(() => setIsIdVerificationOpen(false), [])

  const { run: onOpenPatientChartPage } = useSingleFlightCallback(
    useCallback(() => {
      const link = `${window.location.origin}/patientcharts/patient?id=${cachedPatient?.PatientId}&ref=${EventLocations.inProgressVisit}`
      addSentryBreadcrumb({
        category: 'patient-resource-open',
        message: 'Opened patient chart',
        level: 'info',
        data: { feature: 'patient-chart', patientId: cachedPatient?.PatientId },
      })
      window.open(link, ...windowOpenArgs)
    }, [cachedPatient?.PatientId]),
    { cooldownMs: OPEN_COOLDOWN_MS },
  )

  return {
    cachedPatient,
    showVisitWave,
    isDrugScreenOpen,
    isDrugScreenOpening,
    drugScreenView,
    setDrugScreenView,
    isIdVerificationOpen,
    isIdVerificationOpening,
    openDrugScreen,
    closeDrugScreen,
    openIdVerification,
    closeIdVerification,
    onOpenPatientChartPage,
  }
}
